import React, { FC, forwardRef, ReactNode, useEffect, useRef, useState } from 'react'
import StateManagedSelect, { ActionMeta, components, SingleValue } from 'react-select'
import CreatableSelect, { CreatableProps as CreatableSelectProps } from 'react-select/creatable'
import { applyRefs } from 'apply-ref'

import { doOptionsContainValue, injectOption, Option } from '../../../../lib/options'
import { CustomMenuList } from './CustomMenuList'

type Props = Omit<CreatableSelectProps<Option, false, any>, 'isValidNewOption'> & {
  value?: SingleValue<Option> | undefined
  formatInvalidCreateLabel?: (inputValue: string) => ReactNode
  // Overwrite props to only accept Option[], not GroupedOptionsType<Option>
  // If we ever need to support grouped options, we should set that explicitly with a new prop
  options: Option[]
  onChange: (option: Option | null, actionMeta: ActionMeta<Option | null>) => void
  isValidNewOption?: (inputValue: string, value: Option, options: Option[]) => boolean
}

type InnerSelectProps = CreatableSelectProps<any, any, any>

// Allows editing input value even after an option has been selected
// - based on this solution: https://github.com/JedWatson/react-select/issues/4030#issuecomment-647070013
export const RSCreatableSelectWithEditableValue: FC<Props> = forwardRef<CreatableSelect, Props>(
  function RSCreatableSelectWithEditableValue(
    { value: selectedOption, isClearable = true, ...props },
    ref
  ) {
    const [inputValue, setInputValue] = useState(selectedOption?.label ?? '')
    useEffect(() => setInputValue(selectedOption?.label ?? ''), [selectedOption?.label]) // React to top-down changes

    const selectRef = useRef<StateManagedSelect>(null)

    const handleChange: InnerSelectProps['onChange'] = (option, actionMeta) => {
      props.onChange?.(option, actionMeta)
      setInputValue(option?.label ?? '')
    }

    const handleInputChange: InnerSelectProps['onInputChange'] = (value, meta) => {
      if (meta.action === 'input-change') {
        setInputValue(value)
      }

      props.onInputChange?.(value, meta)
    }

    const handleBlur: InnerSelectProps['onBlur'] = e => {
      setInputValue(selectedOption?.label ?? '')
      props.onBlur?.(e)
    }

    const handleKeyDown: InnerSelectProps['onKeyDown'] = e => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        // @ts-ignore
        selectRef?.current?.blur?.()
      }

      props.onKeyDown?.(e)
    }

    const filterOption = (option: Option, inputValue: string) => {
      if (props.filterOption) {
        return props.filterOption(option as any, inputValue)
      }

      if (!inputValue || inputValue === option.label) {
        return true
      }

      return option.label.toLowerCase().includes(inputValue?.toLowerCase())
    }

    const isSelectedValueMissingInOptions =
      selectedOption?.value && !doOptionsContainValue(props.options, selectedOption?.value)

    const options: Option[] = isSelectedValueMissingInOptions
      ? injectOption(props.options, selectedOption)
      : props.options

    const isInputValuePresentInOptions = !!options?.some(
      option => option?.value.toLowerCase() === inputValue.toLowerCase()
    )

    const isValidNewOption: InnerSelectProps['isValidNewOption'] = (...params) => {
      if (props.isValidNewOption) {
        // @ts-ignore
        return props.isValidNewOption(...params)
      }

      if (isInputValuePresentInOptions) {
        return false
      }

      return !!inputValue
    }

    const invalidCreateLabel = props.formatInvalidCreateLabel?.(inputValue)

    return (
      <CreatableSelect
        ref={applyRefs(selectRef, ref) as any}
        inputValue={inputValue}
        formatCreateLabel={inputValue => {
          return props.formatCreateLabel?.(inputValue) ?? `Create "${inputValue}"`
        }}
        controlShouldRenderValue={false}
        filterOption={filterOption}
        {...props}
        value={selectedOption}
        // These should not be overwritten by props, instead they reflect props internally
        options={options as InnerSelectProps['options']}
        isClearable={isClearable}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        isValidNewOption={isValidNewOption}
        onInputChange={handleInputChange}
        components={{
          Input: CustomInput,
          MenuList: props => (
            // @ts-ignore
            <CustomMenuList {...props} cannotCreateOptionReason={invalidCreateLabel} />
          ),
          ...props.components
        }}
      />
    )
  }
)

const CustomInput: typeof components.Input = props => (
  <components.Input {...props} isHidden={false} />
)

export type RSCreatableSelectWithEditableValueProps = Props
